import { ReactNode, useMemo, useState } from "react";
import { Bell, User, ChevronDown, LogOut, Menu, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import AgbazaButton from "../../../components/atoms/button/Button";
import { cn } from "../../../lib/utils";
import { usePatientAuth } from "../../../store/authPatientStore";

type PatientDashboardLayoutProps = {
  children: ReactNode;
};

const sidebarCancelClass = (active: boolean) =>
  cn(
    "!mt-0 w-full justify-start px-3 py-2 text-sm font-medium laptop:rounded-[8px]",
    active ? "border-l-4 border-[#00553E] bg-[#DCEFE9]/30" : ""
  );

export function withPatientDashboardLayout(children: ReactNode) {
  return <PatientDashboardLayout>{children}</PatientDashboardLayout>;
}

export default function PatientDashboardLayout({ children }: PatientDashboardLayoutProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { patient, signOut, enfants, activeProfil, setActiveProfil } = usePatientAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const displayName = useMemo(() => {
    if (activeProfil) {
      const full = `${activeProfil.nom} ${activeProfil.prenom}`.trim();
      if (full) return full;
    }
    const full = `${patient?.nom ?? ""} ${patient?.prenom ?? ""}`.trim();
    return full || "Patient";
  }, [activeProfil, patient?.nom, patient?.prenom]);

  const selectSelf = () => {
    if (!patient?.nip) return;
    setActiveProfil({ nip: patient.nip, nom: patient.nom ?? "", prenom: patient.prenom ?? "", isEnfant: false });
  };

  const navHome = pathname === "/patient" || pathname === "/patient/";
  const navDossier = pathname.startsWith("/patient/dossier");
  const navConsult = pathname.startsWith("/patient/consultations");
  const navOrdo = pathname.startsWith("/patient/ordonnances");
  const navVacc = pathname.startsWith("/patient/vaccinations");
  const navSante = pathname.startsWith("/patient/sante");
  const navVerif = pathname.startsWith("/patient/verification-identite");
  const navContactsSurete = pathname.startsWith("/patient/contacts-surete");

  const onSignOut = async () => {
    await signOut();
    navigate("/patient/connexion", { replace: true });
  };

  return (
    <div className="h-screen grid grid-cols-1 md:grid-cols-12 grid-rows-1 overflow-hidden">
      <aside
        className="hidden md:flex md:col-span-3 lg:col-span-2 h-full overflow-hidden border-r bg-white p-4 md:p-6 flex-col relative"
        style={{ borderColor: "#E5E7EB" }}
      >
        <div className="flex items-center justify-between">
          <div className="relative">
            <Button
              variant="ghost"
              onClick={() => setMenuOpen((open) => !open)}
              className="flex h-auto items-center gap-2 rounded-[10px] px-2 py-1.5 text-[#0B1F16] hover:bg-[#F3F4F4]"
            >
              <User size={20} className="shrink-0 text-[#00553E]" aria-hidden />
              <span className="font-semibold max-w-[10rem] truncate">{displayName}</span>
              <ChevronDown size={16} className="shrink-0 text-gray-600" aria-hidden />
            </Button>
            {menuOpen && (
              <div
                className="absolute left-0 z-30 mt-2 w-64 min-w-[16rem] max-w-[calc(100vw-2rem)] rounded-[10px] border bg-white p-3 shadow-md"
                style={{ borderColor: "#E5E7EB" }}
              >
                <div className="px-1 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Profils
                </div>
                <AgbazaButton
                  variant="cancel"
                  cancelCustomColor={!activeProfil || activeProfil.isEnfant}
                  expandWidth
                  className={sidebarCancelClass(!!activeProfil && !activeProfil.isEnfant)}
                  style={{ height: "auto", minHeight: 40 }}
                  onClick={() => {
                    selectSelf();
                    setMenuOpen(false);
                  }}
                >
                  Moi ({`${patient?.nom ?? ""} ${patient?.prenom ?? ""}`.trim() || "mon compte"})
                </AgbazaButton>
                {enfants.map((enfant) => (
                  <AgbazaButton
                    key={enfant.nip}
                    variant="cancel"
                    cancelCustomColor={!(activeProfil?.isEnfant && activeProfil.nip === enfant.nip)}
                    expandWidth
                    className={sidebarCancelClass(!!activeProfil?.isEnfant && activeProfil.nip === enfant.nip)}
                    style={{ height: "auto", minHeight: 40 }}
                    onClick={() => {
                      setActiveProfil({ nip: enfant.nip, nom: enfant.nom, prenom: enfant.prenom, isEnfant: true });
                      setMenuOpen(false);
                    }}
                  >
                    {enfant.nom} {enfant.prenom}
                  </AgbazaButton>
                ))}
                <AgbazaButton
                  variant="cancel"
                  cancelCustomColor
                  expandWidth
                  className="!mt-2 w-full justify-start px-3 py-2 text-sm font-medium text-[#00553E] laptop:rounded-[8px]"
                  style={{ height: "auto", minHeight: 40 }}
                  onClick={() => {
                    setMenuOpen(false);
                    navigate("/patient/enfants/nouveau");
                  }}
                >
                  + Ajouter un profil enfant
                </AgbazaButton>
                <div className="my-2 border-t" style={{ borderColor: "#E5E7EB" }} />
                <AgbazaButton
                  variant="cancel"
                  cancelCustomColor
                  expandWidth
                  className="!mt-0 w-full justify-start px-3 py-2 text-sm font-medium laptop:rounded-[8px]"
                  style={{ height: "auto", minHeight: 44 }}
                  onClick={() => {
                    void onSignOut();
                    setMenuOpen(false);
                  }}
                >
                  Déconnexion
                </AgbazaButton>
              </div>
            )}
          </div>
          <div className="hidden md:block" />
          <Button
            variant="ghost"
            size="icon"
            aria-label="Notifications"
            className="text-[#0B1F16] relative md:hidden"
            onClick={() => setNotifOpen((open) => !open)}
          >
            <Bell size={18} />
          </Button>
        </div>

        <div className="flex-1 flex flex-col justify-center">
          <nav className="flex flex-col gap-6">
            <AgbazaButton
              variant="cancel"
              expandWidth
              className={sidebarCancelClass(navHome)}
              style={{ height: "auto", minHeight: 44 }}
              onClick={() => navigate("/patient")}
            >
              Accueil
            </AgbazaButton>
            <AgbazaButton
              variant="cancel"
              expandWidth
              className={sidebarCancelClass(navDossier)}
              style={{ height: "auto", minHeight: 44 }}
              onClick={() => navigate("/patient/dossier")}
            >
              Dossier médical
            </AgbazaButton>
            <AgbazaButton
              variant="cancel"
              expandWidth
              className={sidebarCancelClass(navConsult)}
              style={{ height: "auto", minHeight: 44 }}
              onClick={() => navigate("/patient/consultations")}
            >
              Consultations
            </AgbazaButton>
            <AgbazaButton
              variant="cancel"
              expandWidth
              className={sidebarCancelClass(navOrdo)}
              style={{ height: "auto", minHeight: 44 }}
              onClick={() => navigate("/patient/ordonnances")}
            >
              Ordonnances
            </AgbazaButton>
            <AgbazaButton
              variant="cancel"
              expandWidth
              className={sidebarCancelClass(navVacc)}
              style={{ height: "auto", minHeight: 44 }}
              onClick={() => navigate("/patient/vaccinations")}
            >
              Vaccinations
            </AgbazaButton>
            <AgbazaButton
              variant="cancel"
              expandWidth
              className={sidebarCancelClass(navSante)}
              style={{ height: "auto", minHeight: 44 }}
              onClick={() => navigate("/patient/sante")}
            >
              Mon espace santé
            </AgbazaButton>
            <AgbazaButton
              variant="cancel"
              expandWidth
              className={sidebarCancelClass(navVerif)}
              style={{ height: "auto", minHeight: 44 }}
              onClick={() => navigate("/patient/verification-identite")}
            >
              Vérification d&apos;identité
            </AgbazaButton>
            <AgbazaButton
              variant="cancel"
              expandWidth
              className={sidebarCancelClass(navContactsSurete)}
              style={{ height: "auto", minHeight: 44 }}
              onClick={() => navigate("/patient/contacts-surete")}
            >
              Contacts de sûreté
            </AgbazaButton>
          </nav>
        </div>

        <AgbazaButton
          variant="cancel"
          cancelCustomColor
          expandWidth
          className="!mt-auto flex w-full items-center justify-start gap-2 px-3 py-2 text-sm"
          style={{ height: "auto", minHeight: 44 }}
          onClick={() => void onSignOut()}
        >
          <LogOut className="size-4 shrink-0" aria-hidden />
          <span>Déconnexion</span>
        </AgbazaButton>
      </aside>

      {mobileNavOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileNavOpen(false)} />
          <div
            className="absolute left-0 top-0 h-full w-72 bg-white border-r p-4 flex flex-col"
            style={{ borderColor: "#E5E7EB" }}
          >
            <div className="flex items-center justify-between">
              <div className="font-semibold text-[#0B1F16] max-w-[180px] truncate">{displayName}</div>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Fermer"
                className="text-[#0B1F16]"
                onClick={() => setMobileNavOpen(false)}
              >
                <X size={18} />
              </Button>
            </div>
            <div className="mt-4">
              <nav className="flex flex-col gap-6">
                <AgbazaButton
                  variant="cancel"
                  expandWidth
                  className={sidebarCancelClass(navHome)}
                  style={{ height: "auto", minHeight: 44 }}
                  onClick={() => {
                    setMobileNavOpen(false);
                    navigate("/patient");
                  }}
                >
                  Accueil
                </AgbazaButton>
                <AgbazaButton
                  variant="cancel"
                  expandWidth
                  className={sidebarCancelClass(navDossier)}
                  style={{ height: "auto", minHeight: 44 }}
                  onClick={() => {
                    setMobileNavOpen(false);
                    navigate("/patient/dossier");
                  }}
                >
                  Dossier médical
                </AgbazaButton>
                <AgbazaButton
                  variant="cancel"
                  expandWidth
                  className={sidebarCancelClass(navConsult)}
                  style={{ height: "auto", minHeight: 44 }}
                  onClick={() => {
                    setMobileNavOpen(false);
                    navigate("/patient/consultations");
                  }}
                >
                  Consultations
                </AgbazaButton>
                <AgbazaButton
                  variant="cancel"
                  expandWidth
                  className={sidebarCancelClass(navOrdo)}
                  style={{ height: "auto", minHeight: 44 }}
                  onClick={() => {
                    setMobileNavOpen(false);
                    navigate("/patient/ordonnances");
                  }}
                >
                  Ordonnances
                </AgbazaButton>
                <AgbazaButton
                  variant="cancel"
                  expandWidth
                  className={sidebarCancelClass(navVacc)}
                  style={{ height: "auto", minHeight: 44 }}
                  onClick={() => {
                    setMobileNavOpen(false);
                    navigate("/patient/vaccinations");
                  }}
                >
                  Vaccinations
                </AgbazaButton>
                <AgbazaButton
                  variant="cancel"
                  expandWidth
                  className={sidebarCancelClass(navSante)}
                  style={{ height: "auto", minHeight: 44 }}
                  onClick={() => {
                    setMobileNavOpen(false);
                    navigate("/patient/sante");
                  }}
                >
                  Mon espace santé
                </AgbazaButton>
                <AgbazaButton
                  variant="cancel"
                  expandWidth
                  className={sidebarCancelClass(navVerif)}
                  style={{ height: "auto", minHeight: 44 }}
                  onClick={() => {
                    setMobileNavOpen(false);
                    navigate("/patient/verification-identite");
                  }}
                >
                  Vérification d&apos;identité
                </AgbazaButton>
                <AgbazaButton
                  variant="cancel"
                  expandWidth
                  className={sidebarCancelClass(navContactsSurete)}
                  style={{ height: "auto", minHeight: 44 }}
                  onClick={() => {
                    setMobileNavOpen(false);
                    navigate("/patient/contacts-surete");
                  }}
                >
                  Contacts de sûreté
                </AgbazaButton>
              </nav>
            </div>
            <AgbazaButton
              variant="cancel"
              cancelCustomColor
              expandWidth
              className="!mt-auto flex w-full items-center justify-start gap-2 px-3 py-2 text-sm"
              style={{ height: "auto", minHeight: 44 }}
              onClick={() => void onSignOut()}
            >
              <LogOut className="size-4 shrink-0" aria-hidden />
              <span>Déconnexion</span>
            </AgbazaButton>
          </div>
        </div>
      )}

      <main className="md:col-span-9 lg:col-span-10 relative min-h-0 overflow-y-auto">
        <div className="relative min-h-full w-full">
          <div className="pointer-events-none absolute inset-0 z-0" aria-hidden>
            <img src="/steto.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-black/20" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src="/agb.png" alt="AGBAZA" className="h-9 w-auto" />
              </div>
              <div className="flex items-center gap-6 text-white">
                <button
                  type="button"
                  aria-label="Notifications"
                  className="relative"
                  onClick={() => setNotifOpen((open) => !open)}
                >
                  <Bell size={20} />
                </button>
                <button
                  type="button"
                  aria-label="Menu"
                  className="md:hidden"
                  onClick={() => setMobileNavOpen(true)}
                >
                  <Menu size={20} />
                </button>
              </div>
            </div>

            {notifOpen && (
              <div
                className="absolute right-6 top-16 z-50 w-80 bg-white rounded-[10px] border shadow"
                style={{ borderColor: "#E5E7EB" }}
              >
                <div className="px-5 pt-4 pb-3">
                  <div className="text-center font-semibold text-[#0B1F16]">Centre de notifications</div>
                </div>
                <div className="border-t" style={{ borderColor: "#E5E7EB" }} />
                <div className="max-h-[360px] overflow-auto">
                  <div className="px-5 py-10 text-center text-gray-600 text-sm">
                    Vous n&rsquo;avez aucune notification.
                  </div>
                </div>
              </div>
            )}

            <div className="mt-4 md:mt-6">{children}</div>
          </div>
        </div>
      </main>
    </div>
  );
}
